import type {
    ElasticSearchAvailableFilters,
    ElasticSearchPerformanceStats,
    PCADisplayType,
} from 'c-sdk';

/**
 * The Combined tab (UL-7974): the Summary layout, impacts only, across classic and digital with
 * the two told apart everywhere. Each display type is fetched exactly as its own Summary fetches
 * it and the results are added here, so classic figures on Combined match the Classic report.
 *
 * Plays are deliberately absent: a classic unit logs one play a day and a digital unit thousands,
 * so they are not comparable on one scale.
 */

export const DISPLAY_TYPES: PCADisplayType[] = ['Classic', 'Display'];

export type ByType<T> = Partial<Record<PCADisplayType, T>>;

type StatsMap = Record<string, Partial<ElasticSearchPerformanceStats>>;

const num = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Adds every numeric stat; `performanceStatsToOldCampaignStats` derives percentages from these. */
export const sumStats = (
    stats: Array<Partial<ElasticSearchPerformanceStats> | undefined>,
): ElasticSearchPerformanceStats => {
    const out: Record<string, number> = {};
    stats.forEach(s => {
        Object.entries(s ?? {}).forEach(([k, v]) => {
            if (typeof v === 'number' || (typeof v === 'string' && v !== '' && !Number.isNaN(+v))) {
                out[k] = (out[k] ?? 0) + num(v);
            }
        });
    });
    return out as unknown as ElasticSearchPerformanceStats;
};

export type SplitRow = {
    key: string;
    label: string;
    expected: number;
    expectedClassic: number;
    expectedDigital: number;
    classic: number;
    digital: number;
    total: number;
};

// All delivered impacts, as the Summary's charts and the Total on the card count them -- not just
// in-schedule, or the bars stop adding up to the card.
const delivered = (s?: Partial<ElasticSearchPerformanceStats>) => num(s?.impacts);
const expected = (s?: Partial<ElasticSearchPerformanceStats>) =>
    num(s?.expected_impacts_in_schedule);

/** One row per key: Expected beside a Total split into its classic and digital parts. */
export const splitRows = (maps: ByType<StatsMap>): SplitRow[] => {
    const keys = new Set<string>();
    DISPLAY_TYPES.forEach(t => Object.keys(maps[t] ?? {}).forEach(k => keys.add(k)));
    return [...keys].map(key => {
        const classic = delivered(maps.Classic?.[key]);
        const digital = delivered(maps.Display?.[key]);
        const expectedClassic = expected(maps.Classic?.[key]);
        const expectedDigital = expected(maps.Display?.[key]);
        return {
            key,
            label: key,
            expected: expectedClassic + expectedDigital,
            expectedClassic,
            expectedDigital,
            classic,
            digital,
            total: classic + digital,
        };
    });
};

export const byTotalDesc = (a: SplitRow, b: SplitRow) => b.total - a.total;

export const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

/** Day-of-week keys arrive as names; order them as a week rather than by size. */
export const byDayOfWeek = (a: SplitRow, b: SplitRow) =>
    DAYS_OF_WEEK.indexOf(a.key) - DAYS_OF_WEEK.indexOf(b.key);

export const byHour = (a: SplitRow, b: SplitRow) => Number(a.key) - Number(b.key);

export type RunningTotalRow = {
    date: string;
    expected: number;
    classic: number;
    digital: number;
    total: number;
};

/** The latest day anything was delivered, across every type shown. */
export const dataThrough = (daily: ByType<StatsMap>): string | null =>
    splitRows(daily)
        .filter(r => r.total > 0)
        .map(r => r.key)
        .sort()
        .pop() ?? null;

/**
 * Cumulative delivered against expected; classic and digital stack to the total.
 *
 * The final PCA day is partial while a campaign is running: valid in totals, but drawn on a daily
 * series it is a false dip. It is left off here and only here -- the card and the by-field charts
 * are totals and keep it.
 */
export const runningTotal = (
    daily: ByType<StatsMap>,
    { campaignEnded }: { campaignEnded: boolean },
): RunningTotalRow[] => {
    let rows = splitRows(daily).sort((a, b) => a.key.localeCompare(b.key));
    const last = dataThrough(daily);
    if (!campaignEnded && last) rows = rows.filter(r => r.key < last);

    let exp = 0;
    let classic = 0;
    let digital = 0;
    return rows.map(r => {
        exp += r.expected;
        classic += r.classic;
        digital += r.digital;
        return { date: r.key, expected: exp, classic, digital, total: classic + digital };
    });
};

/** A leaf of a report grouped by several fields carries each group-by value on itself. */
export const collectLeaves = (
    node: unknown,
    out: Array<Record<string, unknown>> = [],
): Array<Record<string, unknown>> => {
    if (node == null || typeof node !== 'object') return out;
    if ('display_count' in (node as Record<string, unknown>)) {
        out.push(node as Record<string, unknown>);
        return out;
    }
    Object.values(node as Record<string, unknown>).forEach(child => collectLeaves(child, out));
    return out;
};

/** Re-keys grouped-by-one-field leaves by that field's value. */
export const keyLeavesBy = (leaves: Array<Record<string, unknown>>, field: string): StatsMap =>
    leaves.reduce((acc, leaf) => {
        const k = String(leaf[field] ?? '');
        acc[k] = sumStats([acc[k], leaf as Partial<ElasticSearchPerformanceStats>]);
        return acc;
    }, {} as StatsMap);

/** The filter options of every display type together, so each type's values can be picked. */
export const mergeFilterOptions = (all: Array<ElasticSearchAvailableFilters | undefined>) =>
    all.reduce((acc, f) => {
        Object.entries(f ?? {}).forEach(([field, values]) => {
            acc[field] = { ...(acc[field] ?? {}), ...(values ?? {}) };
        });
        return acc;
    }, {} as Record<string, Record<string, boolean>>) as ElasticSearchAvailableFilters;
